/**
 * The handful of icons the app needs, drawn inline.
 *
 * No icon library: the charts are hand-rolled SVG already and the only other
 * glyphs in the app are the `↑`/`↓` in `PackageCard`, so five paths do not
 * justify a runtime dependency. All of them are 20×20, stroked with
 * `currentColor`, and `aria-hidden` — every caller is a button or a label that
 * carries the accessible name itself.
 */

type IconProps = { className?: string };

const BASE = {
  viewBox: "0 0 20 20",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** Scenario settings — a sliders panel. */
export function SettingsIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <path d="M3 6h9M15.5 6H17M3 14h1.5M8 14h9" />
      <circle cx="13.75" cy="6" r="2.25" />
      <circle cx="6.25" cy="14" r="2.25" />
    </svg>
  );
}

/** Packages — a box seen in three-quarter view. */
export function PackagesIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <path d="M10 2.5 17.5 6v8L10 17.5 2.5 14V6z" />
      <path d="M2.5 6 10 9.5 17.5 6M10 9.5v8" />
    </svg>
  );
}

/** Costs and revenues — a ledger with ruled lines. */
export function LedgerIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <path d="M4.5 2.5h11v15h-11z" />
      <path d="M7.5 6.5h5M7.5 10h5M7.5 13.5h3" />
    </svg>
  );
}

/** Adoption and churn — a line climbing over time. */
export function AdoptionIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <path d="M3 16.5V3.5M3 16.5h14" />
      <path d="M5.5 13.5 9 9.5l2.75 2.5L17 5.5" />
    </svg>
  );
}

/** A disclosure caret, for a control that opens something under itself. */
export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <path d="m5.5 8 4.5 4.5L14.5 8" />
    </svg>
  );
}

/** The tooltip trigger — a lowercase `i` in a ring. */
export function InfoIcon({ className }: IconProps) {
  return (
    <svg {...BASE} className={className}>
      <circle cx="10" cy="10" r="7.25" />
      <path d="M10 9.25v4.25" />
      <path d="M10 6.5h.01" strokeWidth={2.2} />
    </svg>
  );
}
